import type { Path, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import type { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { mergeHotelPricePartitionsByRoomOptions } from '@/utils/benefits/hotel/mergeHotelPricePartitionsByRoomOptions';
import {
  blankNewRoomPartitions,
  getTickedRoomOptions,
  type RoomOptions,
} from '@/utils/benefits/hotel/excelPaste/parseRoomTariffBlock';
import {
  ROOM_OPTIONS_FIELD,
  ROOM_TYPE_KEYS,
  type RoomTypeKey,
} from '@/utils/benefits/hotel/excelPaste/roomCompositions';

type FormData = HotelFormDataWithTranslations;
export type Category = FormData['hotelRoomCategories'][number];
export type Tariff = Category['tarifs'][number];
export type Partition = Tariff['pricePartitions']['individual'][number];

export interface RoomOptionsForm {
  getValues: UseFormGetValues<FormData>;
  setValue: UseFormSetValue<FormData>;
}

const setOptions = { shouldDirty: true, shouldValidate: true };

/**
 * Lets the repartition effects of mounted tariff cards run, including the
 * renders they trigger themselves (RoomCategoryTypes rewrites options).
 */
export const waitForEffects = async () => {
  for (let round = 0; round < 3; round++) {
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
};

export const getRoomOptions = (category: Category): RoomOptions =>
  Object.fromEntries(
    ROOM_TYPE_KEYS.map((roomType) => [
      roomType,
      [...((category[ROOM_OPTIONS_FIELD[roomType]] as number[]) ?? [])],
    ]),
  ) as RoomOptions;

/** Options of the ticked room types only (see getTickedRoomOptions). */
export const getCategoryTickedOptions = (category: Category): RoomOptions =>
  getTickedRoomOptions(getRoomOptions(category), category.roomTypes ?? {});

/** What the partition effects would create for a newly ticked composition. */
export const createDefaultPartition = (tariff: Tariff) => (): Partition =>
  ({
    price:
      tariff.defaultPricesExist === 'true' && tariff.defaultPrices?.price
        ? Number(tariff.defaultPrices.price)
        : undefined,
    firstSpecialPrice: undefined,
    secondSpecialPrice: undefined,
  }) as Partition;

const sameOptions = (a: number[], b: number[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const union = (a: RoomOptions, b: RoomOptions): RoomOptions =>
  Object.fromEntries(
    ROOM_TYPE_KEYS.map((roomType) => [
      roomType,
      [...new Set([...a[roomType], ...b[roomType]])].sort((x, y) => x - y),
    ]),
  ) as RoomOptions;

/**
 * Moves the category options from `previous` to `target` in one direction
 * (only additions, or only removals), then realigns what the repartition
 * effects did not.
 *
 * Mounted tariff cards re-merge their partitions when options change, so the
 * options are written first and partitions read back once the effects ran.
 * Folded cards and other categories are not mounted and keep partitions
 * aligned on the previous options: since a step only adds or only removes,
 * a length mismatch tells them apart reliably.
 */
async function applyStep(
  { getValues, setValue }: RoomOptionsForm,
  categoryIndex: number,
  previous: RoomOptions,
  target: RoomOptions,
  { blankAdded }: { blankAdded: boolean },
) {
  const categoryPath = `hotelRoomCategories.${categoryIndex}`;
  const set = (name: string, value: unknown) =>
    setValue(name as Path<FormData>, value as never, setOptions);
  const category = getValues(categoryPath as `hotelRoomCategories.${number}`);
  const stored = getRoomOptions(category);

  let changed = false;
  ROOM_TYPE_KEYS.forEach((roomType: RoomTypeKey) => {
    const ticked = target[roomType].length > 0;
    if (!sameOptions(stored[roomType], target[roomType])) {
      set(`${categoryPath}.${ROOM_OPTIONS_FIELD[roomType]}`, target[roomType]);
      changed = true;
    }
    if (Boolean(category.roomTypes?.[roomType]) !== ticked) {
      set(`${categoryPath}.roomTypes.${roomType}`, ticked);
      changed = true;
    }
  });
  if (!changed) return;
  await waitForEffects();

  const tariffs = (getValues(
    `${categoryPath}.tarifs` as `hotelRoomCategories.${number}.tarifs`,
  ) ?? []) as Tariff[];
  tariffs.forEach((tariff, tariffIndex) => {
    ROOM_TYPE_KEYS.forEach((roomType) => {
      const path = `${categoryPath}.tarifs.${tariffIndex}.pricePartitions.${roomType}`;
      const partitions = (tariff.pricePartitions?.[roomType] ??
        []) as Partition[];
      if (target[roomType].length === 0) {
        if (partitions.length > 0) set(path, []);
        return;
      }
      const aligned =
        partitions.length === target[roomType].length
          ? partitions
          : mergeHotelPricePartitionsByRoomOptions({
              previousRoomOptionIndices: previous[roomType],
              previousPartitions: partitions,
              nextRoomOptionIndices: target[roomType],
              createDefaultPartition: createDefaultPartition(tariff),
            });
      const next = blankAdded
        ? blankNewRoomPartitions(previous[roomType], target[roomType], aligned)
        : aligned;
      if (
        next.length !== partitions.length ||
        next.some((partition, position) => partition !== partitions[position])
      ) {
        set(path, next);
      }
    });
  });
}

/**
 * Sets the ticked compositions of a category (and its room types, ticked when
 * they keep a composition), keeping every tariff's prices on the right
 * compositions. Additions go first, then removals.
 */
export async function updateCategoryRoomOptions(
  form: RoomOptionsForm,
  categoryIndex: number,
  target: RoomOptions,
) {
  const category = form.getValues(
    `hotelRoomCategories.${categoryIndex}` as `hotelRoomCategories.${number}`,
  );
  const current = getCategoryTickedOptions(category);
  const widened = union(current, target);
  await applyStep(form, categoryIndex, current, widened, { blankAdded: true });
  await applyStep(form, categoryIndex, widened, target, { blankAdded: false });
}
